/* ASKING FOR AN ACCOUNT, from outside the company.

   Jay Jung, 2026-10-07: "How about guest register by themselves first and then admin
   review/edit/approve." This is the first half — the asking. Nothing here creates an account;
   it writes a row an admin reads, and the account is still made by hand in Register, where the
   roles are chosen.

   THE SAME THANK-YOU WHATEVER HAPPENS. Whether the address already holds an account, already has
   a request open, or the database is down, the page says the same thing. A form that answers "you
   already have an account" tells anyone who asks which addresses hold one, and this page faces
   the open internet.

   It wears the login page's own chrome — the week's photograph, the glass card, the wordmark on
   the picture — because it IS the login page as far as the person standing in front of it is
   concerned, and a form on a bare white page would read as a different site. */
import "./style.css";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Handshake } from "lucide-react";
import logo from "./Helinox- _White.png";
import { backgroundForThisWeek } from "./loginBackgrounds";
import { BACKEND_URL } from "./backendConfig";
import { useTranslation } from "react-i18next";

const FIELDS = [
    /* TWO BOXES — Max, 2026-10-07: "điền first name và last name thay cho your name". Register
       takes the halves separately, and the queue used to GUESS them by splitting this box on a
       space: wrong for a Vietnamese or Korean name, where the family name comes first. Nobody
       but the person typing knows which half is which, so they are asked. */
    /* `labelKey` / `placeholderKey` are translated where the field is drawn; a literal `placeholder` is shown as is. */
    { k: 'firstName', labelKey: 'xAccess.req.firstName', required: true, placeholder: '' },
    { k: 'lastName', labelKey: 'xAccess.req.lastName', required: true, placeholder: '' },
    { k: 'email', labelKey: 'xAccess.req.email', required: true, type: 'email', placeholder: 'name@company.com' },
    { k: 'company', labelKey: 'xAccess.req.company', required: true, placeholder: '' },
    /* The question an admin would otherwise have to ask by email before deciding anything, so it
       is asked once, here, by the person who knows the answer. */
    { k: 'contact', labelKey: 'xAccess.req.contact', required: false, placeholderKey: 'xAccess.req.contactPh' },
];

function RequestAccess() {
    const { t } = useTranslation();
    const [values, setValues] = useState({ firstName: '', lastName: '', email: '', company: '', contact: '', reason: '' });
    const [sent, setSent] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const set = (k, v) => setValues((p) => ({ ...p, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const r = await fetch(`${BACKEND_URL}/api/access-request`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(values),
            });
            const body = await r.json().catch(() => ({}));
            if (!r.ok) {
                setError(body.error || t('xAccess.req.error'));
            } else {
                setSent(true);
            }
        } catch (err) {
            setError(t('xAccess.req.noReach'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-page" style={{ backgroundImage: `url(${backgroundForThisWeek()})` }}>
            <header className="login-topbar">
                <div className="login-topbar-brand">
                    <img src={logo} alt="Helinox" className="login-logo" />
                    <span>HEVN PORTAL</span>
                </div>
            </header>

            <main className="login-card login-card-request">
                <span className="login-mark login-mark-icon" aria-hidden="true">
                    <Handshake size={36} strokeWidth={1.5} />
                </span>
                <h1 className="login-title">{t('xAccess.req.title')}</h1>
                {sent ? (
                    <>
                        <p className="login-subtitle">
                            {t('xAccess.req.thanks')}
                        </p>
                        <p className="login-card-foot">
                            <Link to="/login">{t('xAccess.req.backToSignIn')}</Link>
                        </p>
                    </>
                ) : (
                    <>
                        {/* NO LEGEND UNDER THE SUBTITLE — Max, 2026-10-07: "bỏ cái * required
                            nhé". It was there to explain the star while the star was cyan and
                            had to be hunted for; in red it is the convention everybody already
                            reads, so the line explained a thing nobody needed explaining and
                            sat between the subtitle and the form saying it. */}
                        <p className="login-subtitle">
                            {t('xAccess.req.subtitle')}
                        </p>
                        <div className="login-card-body">
                            <form className="login-form" onSubmit={submit}>
                                {FIELDS.map((f) => (
                                    <div className="login-field-group" key={f.k}>
                                        <span className="login-field-label" id={`${f.k}-label`}>
                                            {t(f.labelKey)}
                                            {/* SHOWN, not merely enforced — Max, 2026-10-07: "3
                                                trường này bắt buộc nhé". They already were, in
                                                the form AND on the server, but nothing on the
                                                screen said so: somebody finds out by pressing
                                                Send and being stopped, which is the worst moment
                                                to learn it. aria-hidden because the input's own
                                                required attribute is what a screen reader reads;
                                                the star is for the eye. */}
                                            {f.required && (
                                                <span className="login-req" aria-hidden="true">*</span>
                                            )}
                                        </span>
                                        <input
                                            id={f.k}
                                            type={f.type || 'text'}
                                            aria-labelledby={`${f.k}-label`}
                                            className="login-field"
                                            placeholder={f.placeholderKey ? t(f.placeholderKey) : f.placeholder}
                                            value={values[f.k]}
                                            onChange={(e) => set(f.k, e.target.value)}
                                            required={f.required}
                                        />
                                    </div>
                                ))}
                                {/* Free text, because what somebody needs the portal FOR is the
                                    thing an admin is actually deciding on, and no dropdown we
                                    could write would hold it. */}
                                <div className="login-field-group login-field-group-tall">
                                    <span className="login-field-label" id="reason-label">
                                        {t('xAccess.req.whatFor')}
                                    </span>
                                    <textarea
                                        id="reason"
                                        aria-labelledby="reason-label"
                                        className="login-field"
                                        rows={3}
                                        placeholder={t('xAccess.req.whatForPh')}
                                        value={values.reason}
                                        onChange={(e) => set('reason', e.target.value)}
                                    />
                                </div>
                                {error && <div className="login-error-message">{error}</div>}
                                <button type="submit" className="login-submit-button" disabled={loading}>
                                    {loading ? t('xAccess.req.sending') : t('xAccess.req.send')}
                                </button>
                            </form>
                        </div>
                        <p className="login-card-foot">
                            <Link to="/login">{t('xAccess.req.backToSignIn')}</Link>
                        </p>
                    </>
                )}
            </main>
        </div>
    );
}

export default RequestAccess;
